import { Box, MenuItem, Select, Typography } from '@mui/material';
import { useIntl, FormattedMessage } from 'react-intl';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { dicomColorToRgbString } from '~/utils/color';
import { ConstraintToggleRow } from './constraintParts';
import { mutedNoteSx, warningSx } from './constraintStyles';

/**
 * Pick the default bounding structure when the constraint is enabled:
 *  1. The EXTERNAL (Body/External patient contour) structure, if present.
 *  2. A structure literally named "body" or "external" (case-insensitive),
 *     covering data where the interpreted type is not set.
 *  3. Otherwise the alphabetically first candidate.
 *
 * Returns `null` when there are no candidates.
 */
export function pickDefaultBoundingRoi(
    candidates: readonly RoiReadModel[]
): number | null {
    if (candidates.length === 0) {
        return null;
    }
    const external = candidates.find(
        (r) => r.interpretedType === RoiInterpretedType.EXTERNAL
    );
    if (external) {
        return external.roiNumber;
    }
    const byName = candidates.find((r) => {
        const name = r.name.trim().toLowerCase();
        return name === 'body' || name === 'external';
    });
    if (byName) {
        return byName.roiNumber;
    }
    const [first] = [...candidates].sort((a, b) =>
        a.name.localeCompare(b.name)
    );
    return first.roiNumber;
}

interface InsideConstraintSectionProps {
    /** Whether the Inside (bounding) constraint is enabled. */
    enabled: boolean;
    /** roiNumber of the selected bounding structure, or null. */
    roiNumber: number | null;
    /** Structures selectable as the bounding structure. */
    candidates: readonly RoiReadModel[];
    /** Enabled but the selected structure is unavailable in this set. */
    inactive: boolean;
    /**
     * Erase mode is active: the selection is persisted but not applied to the
     * stroke. The controls stay interactive so the user can configure them for
     * the next draw; a note explains they are dormant right now.
     */
    erasing: boolean;
    onToggle: (enabled: boolean) => void;
    onSelect: (roiNumber: number) => void;
}

/**
 * Inside (bounding) constraint: clip drawing to the interior of one structure.
 * Presentational — all state lives in the parent orchestrator.
 */
export default function InsideConstraintSection({
    enabled,
    roiNumber,
    candidates,
    inactive,
    erasing,
    onToggle,
    onSelect,
}: InsideConstraintSectionProps) {
    const intl = useIntl();
    const selectedValue =
        roiNumber !== null && candidates.some((r) => r.roiNumber === roiNumber)
            ? roiNumber
            : '';

    return (
        <>
            <ConstraintToggleRow
                labelId="boundingConstraintLabel"
                tooltipId="boundingConstraintTooltip"
                checked={enabled}
                onToggle={onToggle}
            />
            {erasing && (
                <Typography sx={mutedNoteSx}>
                    <FormattedMessage id="constraintNotAppliedWhileErasing" />
                </Typography>
            )}
            {enabled && (
                <Select<number | ''>
                    value={selectedValue}
                    onChange={(e) => onSelect(Number(e.target.value))}
                    disabled={candidates.length === 0}
                    displayEmpty
                    size="small"
                    fullWidth
                    renderValue={(value) => {
                        if (value === '') {
                            return intl.formatMessage({
                                id: 'boundingConstraintPlaceholder',
                            });
                        }
                        const roi = candidates.find(
                            (r) => r.roiNumber === value
                        );
                        if (!roi) {
                            return '';
                        }
                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                }}
                            >
                                <StructureSwatch
                                    color={dicomColorToRgbString(roi.color)}
                                />
                                <span>{roi.name}</span>
                            </Box>
                        );
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                    sx={{ mt: 0.5, fontSize: 12 }}
                >
                    {candidates.map((roi) => (
                        <MenuItem
                            key={roi.roiNumber}
                            value={roi.roiNumber}
                            sx={{
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            <StructureSwatch
                                color={dicomColorToRgbString(roi.color)}
                            />
                            {roi.name}
                        </MenuItem>
                    ))}
                </Select>
            )}
            {inactive && (
                <Typography sx={warningSx}>
                    <FormattedMessage id="constraintInactiveInside" />
                </Typography>
            )}
        </>
    );
}
